import { useEffect, useRef } from 'react'
import { useSidepanel } from '@renderer/overlay/atoms'
import { useQuickTourActive } from '../atoms'
import { useViewActions, useViewNavigation } from '../view-atoms'
import { viewContextOf } from '../view-definitions'
import { GlobalEvents } from '@highlight/ipc'
import { ChatInputElementId, useIsShowingMentions } from '@renderer/chat/components'
import { isInputFocused } from '@renderer/utils/is-interactive-focused'
import { stepToRow } from '../utils/row-step'
import { pulseChatInput } from './use-chat-input-pulse'
import { isMac } from '@shared/utils/platform'

function getCellElements(): Array<HTMLElement> {
  // When a view is open, find cells within it; otherwise fall back to home actions
  const topViewEl = document.querySelector<HTMLElement>('[data-view-top="true"]')
  const container = topViewEl ?? document
  const selector = topViewEl ? '[data-slot="cell"]' : '[data-section] [data-slot="cell"]'
  return Array.from(container.querySelectorAll<HTMLElement>(selector))
}

// Extra scroll margins so the header/chat input don't obscure the focused cell
const SCROLL_TOP_MARGIN = 120
const SCROLL_BOTTOM_MARGIN = 148

function findScrollParent(el: HTMLElement): HTMLElement | null {
  let current = el.parentElement
  while (current) {
    const overflow = getComputedStyle(current).overflowY
    if (overflow === 'auto' || overflow === 'scroll') return current
    current = current.parentElement
  }
  return null
}

function scrollCellIntoView(cell: HTMLElement) {
  const scrollParent = findScrollParent(cell)
  if (!scrollParent) {
    cell.scrollIntoView({ block: 'nearest' })
    return
  }

  const cellRect = cell.getBoundingClientRect()
  const parentRect = scrollParent.getBoundingClientRect()

  // Check if cell is above the visible area (with margin for header/toolbar)
  if (cellRect.top < parentRect.top + SCROLL_TOP_MARGIN) {
    scrollParent.scrollTop += cellRect.top - (parentRect.top + SCROLL_TOP_MARGIN)
  }
  // Check if cell is below the visible area (with extra margin for the chat input)
  else if (cellRect.bottom > parentRect.bottom - SCROLL_BOTTOM_MARGIN) {
    scrollParent.scrollTop += cellRect.bottom - (parentRect.bottom - SCROLL_BOTTOM_MARGIN)
  }
}

function findScrollChild(el: HTMLElement): HTMLElement | null {
  // BFS for first scrollable descendant
  const queue: Array<HTMLElement> = Array.from(el.children) as Array<HTMLElement>
  while (queue.length > 0) {
    const node = queue.shift()!
    if (!node.children) continue
    const ov = getComputedStyle(node).overflowY
    if (ov === 'auto' || ov === 'scroll') return node
    queue.push(...(Array.from(node.children) as Array<HTMLElement>))
  }
  return null
}

function getScrollContainer(): HTMLElement | null {
  // Try from first cell upward
  const cells = getCellElements()
  if (cells[0]) return findScrollParent(cells[0])

  // No cells — look inside the view or sidepanel for a scrollable child
  const root =
    document.querySelector<HTMLElement>('[data-view-top="true"]') ??
    document.querySelector<HTMLElement>('[data-sidepanel-root]')
  if (!root) return null
  return findScrollChild(root)
}

function scrollToEdge(edge: 'top' | 'bottom') {
  const container = getScrollContainer()
  if (!container) return

  if (edge === 'bottom') {
    // Scroll to bottom, retrying on next frames to handle Virtuoso lazy measurement
    let retries = 3
    function scrollBottom() {
      container.scrollTo({ top: container.scrollHeight, behavior: 'instant' })
      if (--retries > 0) requestAnimationFrame(scrollBottom)
    }
    scrollBottom()
    // Focus last history cell or last cell overall
    const historyCell =
      document.querySelector<HTMLElement>('[data-section="history"] [data-slot="cell"]:last-of-type') ??
      getCellElements().at(-1)
    if (historyCell) historyCell.focus({ preventScroll: true })
    return
  }

  container.scrollTo({ top: 0, behavior: 'instant' })
  const cells = getCellElements()
  if (cells[0]) cells[0].focus({ preventScroll: true })
}

function scrollHalfPage(direction: 'down' | 'up') {
  const container = getScrollContainer()
  if (!container) return
  const delta = Math.round(container.clientHeight / 2)
  container.scrollBy({ top: direction === 'down' ? delta : -delta, behavior: 'instant' })
}

function cycleCellFocus(direction: 1 | -1) {
  const cells = getCellElements()
  if (cells.length === 0) return false

  let currentIndex = cells.findIndex((cell) => cell === document.activeElement)
  // From inside a row's popover, step from its row.
  if (currentIndex === -1) currentIndex = cells.findIndex((cell) => cell.hasAttribute('data-popup-open'))

  let nextIndex: number
  if (currentIndex === -1) {
    // Nothing focused — focus first or last depending on direction
    nextIndex = direction === 1 ? 0 : cells.length - 1
  } else {
    nextIndex = currentIndex + direction
    // Clamp to bounds
    if (nextIndex < 0 || nextIndex >= cells.length) return true
  }

  const cell = cells[nextIndex]
  if (cell) {
    stepToRow(cell, cells[currentIndex])
    scrollCellIntoView(cell)
  }
  return true
}

function dismissFocusedCell(e: KeyboardEvent) {
  if (isInputFocused()) return
  const cell = document.activeElement?.closest<HTMLElement>('[data-slot="cell"]')
  if (!cell || cell.hasAttribute('data-reminders')) return
  e.preventDefault()

  // Move focus to the next sibling before triggering close so focus
  // isn't lost when the dismissed cell animates out and is removed.
  const cells = getCellElements()
  const idx = cells.indexOf(cell)
  if (idx !== -1) {
    const next = cells[idx + 1] ?? cells[idx - 1]
    if (next) {
      next.focus({ preventScroll: true })
      scrollCellIntoView(next)
    }
  }

  cell.querySelector<HTMLElement>('[data-cell-close]')?.click()
}

export function useSidepanelHotkeyHandler() {
  const [sidepanelState] = useSidepanel()
  const { openView, forwardView } = useViewActions()
  const { topView, canGoBack, goBack } = useViewNavigation()
  const isShowingMentions = useIsShowingMentions()
  const isShowingMentionsRef = useRef(isShowingMentions)
  isShowingMentionsRef.current = isShowingMentions
  const quickTourActive = useQuickTourActive()
  const quickTourActiveRef = useRef(quickTourActive)
  quickTourActiveRef.current = quickTourActive

  useEffect(() => {
    // Shared history back/forward actions, reused by the Cmd+[ / Cmd+] keys and the mouse side buttons
    const goBackView = () => {
      if (!canGoBack) return
      goBack()
    }
    const goForwardView = () => {
      forwardView()
    }
    // Shared Escape action, reused by the Escape key and the mouse back button
    const escape = () => {
      // If input is focused, blur it first
      if (isInputFocused()) {
        ;(document.activeElement as HTMLElement)?.blur()
        // If Home is on top, just blur — don't close sidepanel
        if (!canGoBack) return
      }

      if (canGoBack) {
        goBack()
        return
      }

      // The quick tour maps Escape to Skip, and skipping must not also hide the
      // panel. The guard lives here because both listeners sit on `window` in
      // the same phase, so the tour cannot swallow the key itself.
      if (quickTourActiveRef.current) return

      // Delegate to sidepanel's close handler which also resets swipe state
      GlobalEvents.emit('sidepanelHotkeyClose')
    }

    // Toggle a section view: close it when it's already on top, otherwise
    // navigate to it. The router animates replacements itself, so no more
    // close-then-reopen timeout dance.
    const toggleView = (id: 'history' | 'actions' | 'upcoming') => {
      if (topView.id === id) {
        goBack()
        return
      }
      openView(id, undefined, { sourceLabel: 'Home' })
    }

    const keyHandlers: Record<string, (e: KeyboardEvent) => void> = {
      n: (e) => {
        const ctrlOnly = e.ctrlKey && !e.metaKey

        // The @ mention menu owns emacs-style Ctrl+N while it's open
        if (ctrlOnly && isShowingMentionsRef.current) return

        // Ctrl+N cycles down through cells on macOS. Windows has no Cmd key,
        // so there it starts a new chat instead.
        if (ctrlOnly && isMac) {
          e.preventDefault()
          cycleCellFocus(1)
          return
        }

        if (!(isMac ? e.metaKey : ctrlOnly)) return
        e.preventDefault()

        // Already on a fresh chat — there's nowhere newer to go, so acknowledge
        // the press by pulsing/focusing the input instead of navigating.
        const isNewChatOnTop = !!viewContextOf(topView, 'chat')?.isNew
        if (isNewChatOnTop) {
          pulseChatInput({ focus: true })
          return
        }

        // The new entry starts with an empty draft; the outgoing view's unsent
        // input is preserved per history entry (useViewDraftPersistence).
        openView('chat', { isNew: true }, { sourceLabel: 'Home' })
      },

      p: (e) => {
        if (!e.ctrlKey || e.metaKey) return
        if (isShowingMentionsRef.current) return
        e.preventDefault()
        cycleCellFocus(-1)
      },

      h: (e) => {
        if (!e.metaKey) return
        e.preventDefault()
        toggleView('history')
      },

      a: (e) => {
        if (isInputFocused()) return
        if (!e.metaKey) return
        e.preventDefault()
        toggleView('actions')
      },

      ArrowDown: (e) => {
        if (isInputFocused()) return
        e.preventDefault()
        if (e.metaKey) {
          scrollHalfPage('down')
        } else {
          cycleCellFocus(1)
        }
      },

      ArrowUp: (e) => {
        if (isInputFocused()) return
        e.preventDefault()
        if (e.metaKey) {
          scrollToEdge('top')
        } else {
          cycleCellFocus(-1)
        }
      },

      // Cmd+[ : back through view history
      '[': (e) => {
        if (!e.metaKey) return
        if (!canGoBack) return
        e.preventDefault()

        goBackView()
      },

      // Cmd+] : forward through view history
      ']': (e) => {
        if (!e.metaKey) return
        e.preventDefault()

        goForwardView()
      },

      d: (e) => {
        // Cmd+D: dismiss focused cell
        if (e.metaKey && !e.ctrlKey) {
          dismissFocusedCell(e)
          return
        }
        // Ctrl+D: half-page down
        if (e.ctrlKey && !e.metaKey) {
          e.preventDefault()
          scrollHalfPage('down')
        }
      },

      u: (e) => {
        // Cmd+U : open upcoming view
        if (e.metaKey) {
          e.preventDefault()
          toggleView('upcoming')
          return
        }

        // Ctrl+U : half-page up
        if (isInputFocused()) return
        if (!e.ctrlKey) return
        e.preventDefault()
        scrollHalfPage('up')
      },

      Escape: (e) => {
        e.preventDefault()
        escape()
      },

      Delete: dismissFocusedCell,
      Backspace: (e) => {
        if (isInputFocused()) return
        e.preventDefault()
        document.getElementById(ChatInputElementId)?.focus()
      },

      Tab: (e) => {
        if (isInputFocused()) return

        const cells = getCellElements()
        if (cells.length === 0) return

        e.preventDefault()
        cycleCellFocus(e.shiftKey ? -1 : 1)
      },
    }

    function handleKeyDown(e: KeyboardEvent) {
      if (sidepanelState !== 'open') return

      // Don't intercept Ctrl+key combos in text inputs — they map to macOS editing shortcuts
      if (e.ctrlKey && !e.metaKey && isInputFocused() && e.key !== 'n' && e.key !== 'p') return

      const handler = keyHandlers[e.key]
      if (handler) handler(e)
    }

    // Mouse side buttons mirror the history back/forward hotkeys (Cmd+[ / Cmd+])
    function handleMouseDown(e: MouseEvent) {
      if (sidepanelState !== 'open') return

      if (e.button === 3) {
        // back side button → Escape (history back, or close the sidepanel from Home)
        e.preventDefault()
        escape()
      } else if (e.button === 4) {
        // forward side button → history forward (Cmd+])
        e.preventDefault()
        goForwardView()
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    window.addEventListener('mousedown', handleMouseDown)
    return () => {
      window.removeEventListener('keydown', handleKeyDown)
      window.removeEventListener('mousedown', handleMouseDown)
    }
  }, [sidepanelState, topView, openView, forwardView, canGoBack, goBack])
}
